<template>
  <AppDrawer
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="title"
    :subtitle="subtitle"
    size="large"
    hide-footer
    hide-close-button
  >
    <template #header>
      <div class="header-row">
        <div class="header-titles">
          <div class="is-flex is-align-items-center">
            <h3 class="title is-5 mb-0">{{ title }}</h3>
            <span v-if="isWordLevel" class="tag is-primary is-small ml-2">
              <span class="icon is-small mr-1">
                <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
              </span>
              Word-level
            </span>
          </div>
          <p v-if="subtitle" class="subtitle is-7 mb-0 mt-1 has-text-grey">
            {{ subtitle }}
          </p>
        </div>
      </div>
    </template>
    <div class="transcript-drawer">
      <!-- Readable / Word-level view toggle (only when word-level data exists) -->
      <div v-if="hasWordLevel" class="view-toggle mb-2">
        <div class="buttons has-addons mb-0">
          <button
            type="button"
            class="button is-small"
            :class="{ 'is-primary': viewMode === 'readable' }"
            @click="viewMode = 'readable'"
          >Readable</button>
          <button
            type="button"
            class="button is-small"
            :class="{ 'is-primary': viewMode === 'wordLevel' }"
            @click="viewMode = 'wordLevel'"
          >Word-level</button>
        </div>
        <span class="is-size-7 has-text-grey ml-2">
          {{ viewMode === 'wordLevel'
            ? 'Each word carries its own timestamp (inline WebVTT).'
            : 'Grouped into readable caption lines.' }}
        </span>
      </div>

      <div class="transcript-textarea-wrap">
        <textarea
          ref="textareaRef"
          class="textarea is-family-monospace transcript-textarea"
          :value="displayedContent"
          @input="onTextInput"
          :readonly="!editable"
          spellcheck="false"
          rows="24"
          :placeholder="editable ? 'Paste a transcript, or WebVTT / SRT captions…' : 'No transcript yet for this asset.'"
        ></textarea>

        <!-- Floating copy dropdown -->
        <div
          v-if="displayedContent"
          ref="copyMenuRef"
          class="dropdown is-right copy-dropdown"
          :class="{ 'is-active': isCopyMenuOpen }"
        >
          <div class="dropdown-trigger">
            <button
              type="button"
              class="button is-small copy-trigger"
              @click="isCopyMenuOpen = !isCopyMenuOpen"
              :title="copiedRecently ? 'Copied' : 'Copy transcript'"
              aria-haspopup="true"
              aria-controls="copy-menu"
            >
              <span class="icon is-small">
                <font-awesome-icon
                  :icon="copiedRecently ? ['fas', 'check'] : ['fal', 'copy']"
                />
              </span>
            </button>
          </div>
          <div class="dropdown-menu" id="copy-menu" role="menu">
            <div class="dropdown-content">
              <a
                href="#"
                class="dropdown-item"
                :class="{ 'is-disabled': !isVtt }"
                @click.prevent="copyWithoutTimestamps"
              >
                Copy without timestamps
              </a>
              <a
                href="#"
                class="dropdown-item"
                @click.prevent="copyWithTimestamps"
              >
                Copy with timestamps
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button class="button is-text" @click="close">Close</button>

      <!-- Optional caller-supplied action (e.g. Re-transcribe) -->
      <slot name="footer-start" />

      <div class="footer-actions">
        <div class="field has-addons download-group mb-0">
          <p class="control">
            <span class="button is-static">
              <span class="icon is-small">
                <font-awesome-icon :icon="['fal', 'download']" />
              </span>
              <span>Download</span>
            </span>
          </p>
          <p class="control">
            <button
              type="button"
              class="button is-info"
              :disabled="!displayedContent"
              @click="downloadVttFile"
              :title="hasWordLevel
                ? (viewMode === 'wordLevel'
                    ? 'Download WebVTT with inline per-word timestamps (.vtt)'
                    : 'Download the readable grouped WebVTT (.vtt)')
                : 'Download as WebVTT (.vtt)'"
            >
              {{ vttLabel }}
            </button>
          </p>
          <p class="control">
            <button
              type="button"
              class="button is-link"
              :disabled="!readableVtt || !readableIsVtt"
              @click="downloadSrtFile"
              :title="readableIsVtt
                ? 'Download as SubRip (.srt) — readable grouping only (SRT can\'t carry per-word timing)'
                : 'SRT export requires a WebVTT source'"
            >
              {{ srtLabel }}
            </button>
          </p>
        </div>

        <button
          v-if="editable"
          type="button"
          class="button is-primary"
          :class="{ 'is-loading': saving }"
          :disabled="!isDirty || saving"
          @click="save"
        >
          <span class="icon is-small">
            <font-awesome-icon :icon="['fal', 'check']" />
          </span>
          <span>Save</span>
        </button>
      </div>
    </template>
  </AppDrawer>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import AppDrawer from '@/components/global/AppDrawer.vue'
import { vttToPlainText, vttToSrt, countVttCues, looksLikeVtt, looksLikeSrt, srtToVtt, wordsToInlineVtt } from '@/utils/captionsUtils'
import { useToast } from '@/composables/useToast'
import { logger } from '@/services/loggerService'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  vtt: { type: String, default: '' },
  // Used to name downloaded files. Falls back to "transcript".
  filenameBase: { type: String, default: 'transcript' },
  // When true, the textarea is editable and a Save button appears.
  editable: { type: Boolean, default: false },
  // Parent-owned spinner state for the Save button.
  saving: { type: Boolean, default: false },
  // Raw word-level data ({word,start,end}[]) from assetTranscripts/{id}. When
  // present, the Readable/Word-level toggle appears.
  words: { type: Array, default: () => [] },
  // True if this asset has a word-level transcript (drives the header badge).
  wordLevel: { type: Boolean, default: false },
  // Provenance ('grok' | 'elevenlabs' | …) shown in the badge.
  source: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'save'])

const toast = useToast()
const textareaRef = ref(null)
const copyMenuRef = ref(null)
const copiedRecently = ref(false)
const isCopyMenuOpen = ref(false)
// 'readable' (grouped cues) | 'wordLevel' (inline per-word timestamps).
const viewMode = ref('readable')

// Local editable copy. Mirrors the `vtt` prop (fed by the asset's Firestore
// snapshot) and resets whenever that prop changes or the drawer reopens.
const draft = ref(props.vtt)
const isDirty = computed(() => draft.value !== props.vtt)

// Synchronous indicator (from the asset doc) — drives the badge so it shows
// immediately, independent of the async words fetch (which can lag or fail).
const isWordLevel = computed(() => props.wordLevel)
// Raw words actually loaded → gates the toggle + word-level downloads/view.
const hasWordLevel = computed(() => isWordLevel.value && Array.isArray(props.words) && props.words.length > 0)
// Inline-timestamp WebVTT built from the raw words (the "word-level" view).
const wordLevelVtt = computed(() => (hasWordLevel.value ? wordsToInlineVtt(props.words) : ''))
// The readable, grouped VTT — the editable draft when editing, else the prop.
const readableVtt = computed(() => (props.editable ? draft.value : props.vtt))
// What the textarea shows: editing always shows the draft; otherwise the
// selected view (word-level only when it exists).
const displayedContent = computed(() => {
  if (props.editable) return draft.value
  return (viewMode.value === 'wordLevel' && hasWordLevel.value) ? wordLevelVtt.value : props.vtt
})

const isVtt = computed(() => looksLikeVtt(displayedContent.value))
const readableIsVtt = computed(() => looksLikeVtt(readableVtt.value))
const cueCount = computed(() => countVttCues(readableVtt.value))

// SRT is always the readable grouping; VTT follows the toggle.
const vttLabel = computed(() => {
  if (!hasWordLevel.value) return 'VTT'
  return viewMode.value === 'wordLevel' ? 'VTT · word-level' : 'VTT · readable'
})
const srtLabel = computed(() => (hasWordLevel.value ? 'SRT · readable' : 'SRT'))

const onTextInput = (e) => {
  if (props.editable) draft.value = e.target.value
}

const title = computed(() => 'Transcript')
const subtitle = computed(() => {
  // Badge shows for ANY word-level asset (synchronous) — even before/without
  // the raw words loading. Count + view-mode only once the words are present.
  if (isWordLevel.value) {
    if (hasWordLevel.value) {
      const n = props.words.length
      return `Word-level · ${n.toLocaleString()} words · shown ${viewMode.value === 'wordLevel' ? 'per-word' : 'grouped'}`
    }
    return 'Word-level · grouped view'
  }
  if (!displayedContent.value) return props.editable ? 'Add a transcript' : 'No transcript'
  if (isVtt.value) return `${cueCount.value} cue${cueCount.value === 1 ? '' : 's'} · WebVTT`
  return 'Plain text'
})

const safeFilename = (ext) => {
  const base = (props.filenameBase || 'transcript')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'transcript'
  return `${base}.${ext}`
}

const triggerDownload = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Give the browser a tick to start the download before revoking
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const writeToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text)
    copiedRecently.value = true
    isCopyMenuOpen.value = false
    setTimeout(() => { copiedRecently.value = false }, 2000)
    if (label) toast.success(label)
  } catch (err) {
    logger.warn('[TranscriptDrawer] Clipboard write failed', { error: err.message })
    toast.error('Could not copy to clipboard')
  }
}

const copyWithoutTimestamps = () => {
  // Plain words are the same regardless of view — always strip from the
  // readable grouping (inline word tags would otherwise leak into the text).
  if (!readableVtt.value) return
  const text = readableIsVtt.value ? vttToPlainText(readableVtt.value) : readableVtt.value
  writeToClipboard(text, 'Copied transcript text')
}

const copyWithTimestamps = () => {
  // Copies the CURRENT view (readable cues, or inline per-word timestamps).
  if (!displayedContent.value) return
  const label = hasWordLevel.value && viewMode.value === 'wordLevel'
    ? 'Copied word-level WebVTT'
    : (isVtt.value ? 'Copied WebVTT' : 'Copied transcript')
  writeToClipboard(displayedContent.value, label)
}

const downloadVttFile = () => {
  // VTT follows the toggle: word-level (inline timestamps) or readable grouping.
  if (!displayedContent.value) return
  const wordLevelView = hasWordLevel.value && viewMode.value === 'wordLevel'
  triggerDownload(displayedContent.value, safeFilename(wordLevelView ? 'words.vtt' : 'vtt'), 'text/vtt;charset=utf-8')
}

const downloadSrtFile = () => {
  // SRT can't carry per-word timing — always export the readable grouping.
  if (!readableVtt.value || !readableIsVtt.value) return
  const srt = vttToSrt(readableVtt.value)
  triggerDownload(srt, safeFilename('srt'), 'application/x-subrip;charset=utf-8')
}

// Hand the edited text to the parent, which owns persistence. The saved value
// flows back via the `vtt` prop (Firestore snapshot), which resets the draft
// and disables the Save button. `saving` prop drives the spinner.
const save = () => {
  if (!isDirty.value || props.saving) return
  // Normalize pasted SRT to WebVTT so the stored field is always canonical and
  // every downstream feature (cue count, plain-text copy, SRT export) works.
  const text = draft.value.trim()
  emit('save', looksLikeSrt(text) ? srtToVtt(text) : text)
}

const close = () => emit('update:modelValue', false)

// Close the copy menu when clicking outside it.
const onDocumentClick = (event) => {
  if (!isCopyMenuOpen.value) return
  if (copyMenuRef.value && !copyMenuRef.value.contains(event.target)) {
    isCopyMenuOpen.value = false
  }
}

// Keep the draft in sync with the saved transcript (snapshot updates, pulls).
watch(() => props.vtt, (val) => { draft.value = val })

// Reset transient UI state every time the drawer reopens, and discard any
// unsaved edits from a previous session by re-seeding from the saved value.
watch(() => props.modelValue, (open) => {
  if (open) {
    draft.value = props.vtt
    viewMode.value = 'readable'
  } else {
    isCopyMenuOpen.value = false
    copiedRecently.value = false
  }
})

onMounted(() => {
  document.addEventListener('mousedown', onDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', onDocumentClick)
})
</script>

<style lang="scss" scoped>
.transcript-drawer {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.view-toggle {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.header-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
}

.header-titles {
  flex: 1;
  min-width: 0;
}

.header-pull-btn {
  flex-shrink: 0;
  // Nudge slightly to align visually with the title baseline.
  margin-top: 0.125rem;
}

.transcript-textarea-wrap {
  position: relative;
  flex: 1;
  display: flex;
}

.transcript-textarea {
  flex: 1;
  min-height: 60vh;
  font-size: 0.85rem;
  line-height: 1.5;
  resize: none;
  background: var(--bulma-background, #fafafa);
  color: var(--bulma-text);
  border-color: var(--bulma-border);
  // Reserve room so wrapped lines don't slide under the floating button.
  padding-right: 3rem;
}

.copy-dropdown {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;

  .copy-trigger {
    background: var(--bulma-scheme-main, #fff);
    border-color: var(--bulma-border, #dbdbdb);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    &:hover {
      background: var(--bulma-scheme-main-bis, #fafafa);
    }
  }

  .dropdown-item.is-disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

.download-group {
  // The button-group prefix label and buttons should sit flush.
  // Override Bulma's default field margin so it doesn't push the row.
  :deep(.button.is-static) {
    background: var(--bulma-background, #fafafa);
    color: var(--bulma-text-weak, #6b7280);
    font-weight: 500;
  }
}
</style>
